/**
 * Single source of truth for all AI conversation UI strings.
 *
 * Every user-facing string rendered inside Homepage AI (WellnessConcierge),
 * AI Navigator (AINavigatorChat), and Deep Mode (DeepModePanel) MUST come
 * from this module — never inline literals or scattered ternaries.
 *
 * The conversation language follows `i18n.language`. When the user switches
 * language, every string returned by `aiText()` flips immediately because
 * React re-renders on `i18n.language` change.
 *
 * Rules:
 *   1. NEVER add raw English strings in AI UI components.
 *   2. NEVER paraphrase a single string into two — both languages must
 *      describe the same user-facing concept.
 *   3. NEVER emit a meta sentence about switching language (handled in
 *      the edge function system prompt — see ai-navigator/index.ts).
 */

export type AiLang = 'en' | 'zh';

export const aiLangOf = (lang?: string): AiLang =>
  lang?.toLowerCase().startsWith('zh') ? 'zh' : 'en';

type Pair = { en: string; zh: string };

const STRINGS = {
  // ── Brand / header ────────────────────────────────────────────
  brand_concierge_lead:        { en: 'Your Personal ', zh: '你的' },
  brand_concierge_name:        { en: 'Wellness Concierge', zh: '专属疗愈顾问' },
  brand_homepage_intent_q:     { en: 'How would you like Resonow to help you today?', zh: 'Resonow 今天能为你做些什么？' },
  brand_homepage_intent_hint:  { en: 'Pick a direction and we’ll take you into the right conversation.', zh: '选择一个方向，我们会带你进入合适的对话。' },

  // ── Intent / role chooser ─────────────────────────────────────
  intent_client_label:         { en: 'I want support for myself', zh: '我想为自己寻找支持' },
  intent_client_hint:          { en: 'Wellness Concierge · personal guidance', zh: '专属疗愈顾问 · 个性化建议' },
  intent_practitioner_label:   { en: "I'm a practitioner", zh: '我是疗愈师' },
  intent_practitioner_hint:    { en: 'Certification, pricing, dashboard, tools', zh: '认证 · 定价 · Dashboard · 内容工具' },
  intent_learn_label:          { en: 'I want to learn about Resonow', zh: '我想了解 Resonow' },
  intent_learn_hint:           { en: 'Philosophy, framework, platform', zh: '理念 · 框架 · 平台介绍' },

  // ── Practitioner / learn lightweight assistant ───────────────
  asst_practitioner_title:     { en: 'Practitioner Platform Assistant', zh: '疗愈师平台助手' },
  asst_practitioner_subtitle:  { en: 'Certification, pricing, Clinical track, dashboard, content tools — answered from platform docs first', zh: '认证、定价、Clinical 路线、Dashboard、内容创建 — 优先从平台文档作答' },
  asst_learn_title:            { en: 'Learn about Resonow', zh: '了解 Resonow' },
  asst_learn_subtitle:         { en: 'A quick guide to Resonow’s framework and how the platform works', zh: '介绍 Resonow 的框架、流程与平台原理' },
  asst_switch_role:            { en: 'Not what you need? Switch role', zh: '不是你要的?切换身份' },
  asst_try_these:              { en: 'Try one of these:', zh: '试试这些问题:' },
  asst_thinking:               { en: 'Thinking…', zh: '正在思考…' },
  asst_keep_exploring:         { en: 'Keep exploring', zh: '继续探索' },
  asst_gate_msg:               { en: 'To continue this conversation, please sign in to your practitioner account.', zh: '想继续深入聊?请先登录疗愈师账号。' },
  asst_sign_in_up:             { en: 'Sign in / Sign up', zh: '登录 / 注册' },
  asst_switch_role_btn:        { en: 'Switch role', zh: '切换身份' },
  asst_disclaimer:             { en: 'This assistant answers Resonow platform questions only — not medical or therapeutic advice.', zh: '此助手仅回答平台相关问题,不提供医疗或心理咨询建议。' },
  asst_input_platform:         { en: 'Ask a platform question…', zh: '问一个关于平台的问题…' },
  asst_input_learn:            { en: 'What would you like to know about Resonow?', zh: '想了解 Resonow 的什么?' },
  asst_failed:                 { en: 'Assistant failed. Try again.', zh: '助手暂时无法响应,请稍后再试' },

  // ── Starters ──────────────────────────────────────────────────
  starters_practitioner: {
    en: [
      "What’s the difference between Clinical and Wellness tracks?",
      'What do I need to prepare for certification?',
      'How do payouts and fees work?',
    ],
    zh: [
      'Clinical 路线和 Wellness 路线有什么区别？',
      '认证需要准备哪些资料？',
      '收款和分成是怎么算的？',
    ],
  },
  starters_learn: {
    en: [
      'What is Resonow?',
      'How does the Stage + Domain framework work?',
      'How do clients find practitioners?',
    ],
    zh: [
      'Resonow 是什么？',
      'Stage 和 Domain 框架怎么用？',
      '客户是怎么找到疗愈师的？',
    ],
  },

  // ── Concern / duration chips ─────────────────────────────────
  chip_concern_header: { en: "What's been on your mind?", zh: '最近让你困扰的是？' },
  chip_duration_header:{ en: 'How long has this been going on?', zh: '持续了多久？' },
  chip_send_hint:      { en: 'Send now, or add more details below', zh: '可以直接发送，或在下方补充更多细节' },
  chip_analyze:        { en: 'Analyze', zh: '开始分析' },

  // ── Crisis / safety ──────────────────────────────────────────
  crisis_redirect:        { en: 'Redirecting you to support resources…', zh: '正在为您跳转到危机支持资源…' },
  crisis_get_help_title:  { en: 'Please Get Immediate Help', zh: '请立即寻求帮助' },
  crisis_get_help_btn:    { en: 'Get Immediate Help', zh: '获取即时帮助' },

  // ── Conversation chrome ──────────────────────────────────────
  conv_quick_reply:       { en: 'Quick reply', zh: '快速回复' },
  conv_continue:          { en: 'Continue the conversation', zh: '继续对话' },
  conv_collapse:          { en: 'Collapse', zh: '收起对话' },
  conv_expand:            { en: 'Expand conversation', zh: '展开对话' },
  conv_choose_path:       { en: '← Choose a different path', zh: '← 换一个方向' },
  conv_continue_dashboard:{ en: 'Continue this conversation in Dashboard', zh: '在 Dashboard 继续上次对话' },
  conv_disclaimer:        { en: 'For informational use only; not intended as medical, therapeutic, or professional advice.', zh: '仅供参考，不构成医疗或专业建议' },
  conv_share_first:       { en: 'Share a few thoughts with Reso first', zh: '先和 Reso 聊一会儿吧' },
  conv_share_first_short: { en: 'Share a few thoughts first', zh: '先聊几句再深入分析' },

  // ── Branch card (mid-conversation) ───────────────────────────
  branch_intro:           { en: 'From what we’ve talked through so far, I have a clearer sense of what’s going on. Where would you like to go from here?', zh: '根据目前的对话,我已经更清楚地理解了一些正在发生的事。接下来你想怎么走?' },
  branch_continue:        { en: 'Continue exploring', zh: '继续探索' },
  branch_see_practitioners:{ en: 'See recommended practitioners', zh: '查看推荐疗愈师' },
  branch_save_later:      { en: 'Save & continue later', zh: '保存稍后继续' },

  // ── Service recommendations panel ────────────────────────────
  rec_panel_title:        { en: 'Support path from this conversation', zh: '基于当前对话的支持路径' },
  rec_primary_stage:      { en: 'Primary Stage', zh: 'Primary Stage' },
  rec_supportive_stage:   { en: 'Supportive Stage', zh: 'Supportive Stage' },
  rec_view_profile:       { en: 'View profile', zh: '主页' },
  rec_loading:            { en: 'Loading', zh: '加载中' },
  rec_book:               { en: 'Book', zh: '预约' },
  rec_view_bookable:      { en: 'View bookable services', zh: '查看可预约服务' },

  // ── Go deeper / dashboard transition ─────────────────────────
  deep_enter:             { en: 'Entering deep analysis…', zh: '正在进入深度分析…' },
  deep_enter_desc:        { en: 'Your conversation will continue seamlessly', zh: '你的对话会无缝继续' },

  // ── Sign in gate (anonymous client) ──────────────────────────
  gate_sign_in_title:     { en: 'Sign in to keep this conversation going', zh: '继续这段对话，请先登录' },
  gate_sign_in_desc:      { en: 'Your conversation is saved. Sign in to keep exploring, unlock deeper analysis, and get personalized recommendations.', zh: '您的所有内容都已保留。登录后可继续深入分析、保存进度，并获得个性化推荐。' },
  gate_create_account:    { en: 'Create free account', zh: '免费注册' },
  gate_sign_in:           { en: 'Sign in', zh: '登录' },
  gate_continue_toast:    { en: 'Sign in to keep going — your conversation is saved.', zh: '继续对话请登录，您的内容会自动保留。' },
  gate_placeholder:       { en: 'Sign in to continue…', zh: '登录后继续对话…' },

  // ── Input placeholders ───────────────────────────────────────
  input_guided:           { en: 'What’s been on your mind lately?', zh: '最近你心里在想什么？' },
  input_continue:         { en: 'Tell me more about how you feel...', zh: '继续描述你的感受...' },
  input_navigator:        { en: "Share what's on your mind...", zh: '说说你的感受...' },

  // ── AI Navigator dashboard ───────────────────────────────────
  nav_title:              { en: '🧭 AI Navigator', zh: '🧭 AI 导航' },
  nav_go_deeper:          { en: 'Go deeper', zh: '深入' },
  nav_go_deeper_title:    { en: 'Deeper analysis of this conversation', zh: '深入分析当前对话' },
  nav_no_history:         { en: 'No past conversations', zh: '暂无历史对话' },
  nav_empty_title:        { en: "Hi, I'm Reso — Your AI Wellness Navigator", zh: '你好，我是 Reso — 你的 AI 身心导航' },
  nav_empty_desc:         { en: 'I guide you through three phases: ① Understanding your needs & feelings, ② Analyzing potential root causes, ③ Recommending personalized therapies & matching practitioners.', zh: '我会通过三个阶段帮助你：① 深入了解你的身心状态，② 分析可能的根源，③ 为你推荐匹配的疗愈方式与执业者。' },
  nav_empty_hint:         { en: "Pick a topic below to get started, or simply share what's on your mind.", zh: '你可以从下面选择一个话题开始，或直接告诉我你的感受。' },
  nav_disclaimer:         { en: 'Reso is an AI navigator, not a therapist. For emergencies, call your local crisis line.', zh: 'Reso 是 AI 导航者，不提供诊断或治疗。如有紧急情况请拨打急救电话。' },
  nav_voice_start:        { en: 'Voice input', zh: '语音输入' },
  nav_voice_stop:         { en: 'Stop recording', zh: '停止录音' },
  nav_degraded:           { en: 'Recommendations may be incomplete — a lookup failed this turn.', zh: '本轮有一次查询失败，推荐可能不完整。' },
  friction_intro:         {
    en: 'Any system that keeps itself together while changing pays a cost. The question is never whether you pay — it is where you pay. The four readings below show where your effort currently goes, and which of those costs are holding you up versus draining you.',
    zh: '任何在变化中维持自己的系统都要付出代价。问题从来不是「付不付」，而是「付在哪里」。下面四个量显示你的力气目前主要花在哪，以及哪些花销正在支撑你、哪些正在消耗你。',
  },


  nav_starters: {
    en: [
      "I've been feeling anxious lately",
      'I want to explore energy healing',
      'Help me find the right practitioner',
      'What therapies might help with stress?',
    ],
    zh: [
      '最近感到焦虑',
      '想了解能量疗愈',
      '帮我找合适的疗愈师',
      '有什么疗法能缓解压力？',
    ],
  },

  // Deep prompt sent by "Go deeper" inside AI Navigator.
  nav_deep_prompt: {
    en: "Based on everything I've shared so far, please go deeper: surface the underlying patterns you notice, where I might be in the healing journey, and which therapy directions seem most relevant for me. If you need more context, ask the single most useful next question.",
    zh: '请基于我目前分享的所有内容，给出一次更深入的分析：可能的根源模式、所处的疗愈阶段、以及最适合我的疗法方向。如果还需要更多信息，请提出最关键的下一个问题。',
  },

  // ── Deep Mode panel (Framework page) ─────────────────────────
  dm_expired_title:       { en: 'Deep-mode conversation context expired', zh: '深入分析的会话上下文已过期' },
  dm_expired_desc:        { en: 'Head back to your Reso chat, share a bit more, then tap "Go deeper" again.', zh: '请回到 Reso 对话，继续分享后再点击「深入」。' },
  dm_back_to_chat:        { en: 'Back to conversation', zh: '回到对话' },
  dm_back_to_home:        { en: 'Back to home', zh: '回到首页' },
  dm_continue_title:      { en: 'Continue deeper analysis from your conversation', zh: '继续深入分析你的对话' },
  dm_continue_desc:       { en: "We're using your conversation with Reso to map a deeper structural picture.", zh: '我们正用你和 Reso 的对话内容，生成更结构化的能量地图。' },
  dm_back_short:          { en: 'Back to chat', zh: '回到对话' },
  dm_extracting:          { en: 'Extracting evidence from your conversation…', zh: '正在从你的对话中提取证据…' },
  dm_mapping:             { en: 'Mapping resistance sources…', zh: '正在映射阻力来源…' },
  dm_matching:            { en: 'Matching therapy modalities…', zh: '正在匹配疗法…' },
  dm_continue_reso:       { en: 'Continue with Reso', zh: '继续与 Reso 对话' },
  dm_see_practitioners:   { en: 'See recommended practitioners', zh: '查看推荐疗愈师' },
  dm_not_enough_title:    { en: 'Not enough context for deep analysis', zh: '内容不足以深入分析' },
  dm_not_enough_desc:     { en: 'Share a bit more in the chat first.', zh: '请回到对话再多分享一些感受。' },
  dm_analysis_failed:     { en: 'Analysis failed', zh: '分析失败' },
  dm_try_later:           { en: 'Please try again', zh: '请稍后再试' },

  // ── Dynamic follow-up chips (chip stages) ────────────────────
  chips_early: {
    en: ['Tell me a bit more', "What’s been hardest?", 'When did this start?', 'How does it feel in your body?'],
    zh: ['可以再多说一点吗？', '最让你难受的是什么？', '这种感觉是什么时候开始的？', '身体上有什么反应？'],
  },
  chips_mid: {
    en: ['Add more context', 'How often does it happen?', 'What have you already tried?', 'Go deeper with analysis'],
    zh: ['补充更多背景', '它出现得有多频繁？', '你之前尝试过什么方法？', '深入分析'],
  },
  chips_late: {
    en: ['Go deeper with analysis', 'What might help?', 'Show me options', 'Help me understand why'],
    zh: ['深入分析', '有哪些可能有帮助的方式？', '给我看看选项', '帮我理解原因'],
  },
  chips_conversion: {
    en: ['View practitioners', 'Book a session', 'Go deeper with analysis', 'Save for later'],
    zh: ['查看疗愈师', '预约一次咨询', '深入分析', '稍后再看'],
  },

  // Concern category chips
  concern_chips: {
    en: [
      { label: 'Anxiety',      value: 'anxiety' },
      { label: 'Relationship', value: 'relationship difficulties' },
      { label: 'Sleep',        value: 'sleep problems' },
      { label: 'Burnout',      value: 'burnout and exhaustion' },
      { label: 'Feeling lost', value: 'feeling lost' },
    ],
    zh: [
      { label: '焦虑', value: '焦虑' },
      { label: '关系', value: '关系困扰' },
      { label: '睡眠', value: '睡眠问题' },
      { label: '倦怠', value: '倦怠和疲惫' },
      { label: '迷茫', value: '感到迷茫' },
    ],
  },
  duration_chips: {
    en: [
      { label: 'Just now',  value: 'just started recently' },
      { label: 'Weeks',     value: 'for several weeks' },
      { label: 'Months',    value: 'for months' },
      { label: 'Long-term', value: 'for a long time, possibly years' },
    ],
    zh: [
      { label: '刚开始', value: '最近刚开始' },
      { label: '几周了', value: '持续了几周' },
      { label: '几个月', value: '已经几个月了' },
      { label: '长期',   value: '长期存在，可能好几年了' },
    ],
  },

  // Stage names (used inline inside markdown buttons)
  stage_names: {
    en: ['Crisis Intervention', 'Harmonize Mind & Body', 'Analysis & Investigation', 'Transpersonal Dive', 'Actualization & Integration'],
    zh: ['危机干预', '身心调和', '分析与探究', '超个人深潜', '实现与整合'],
  },

  // ── Biometric intake (personalized nutrition) ─────────────────
  bio_panel_title:      { en: 'Personalize nutrition guidance', zh: '个性化营养建议' },
  bio_panel_hint:       { en: 'Add your basics so dosing suggestions match your body instead of a generic average.', zh: '填写基本信息，剂量建议将按你的身体情况给出，而不是笼统的平均值。' },
  bio_panel_open:       { en: 'Add my details', zh: '填写我的信息' },
  bio_panel_edit:       { en: 'Edit my details', zh: '修改我的信息' },
  bio_age:              { en: 'Age', zh: '年龄' },
  bio_sex:              { en: 'Sex at birth', zh: '生理性别' },
  bio_sex_male:         { en: 'Male', zh: '男' },
  bio_sex_female:       { en: 'Female', zh: '女' },
  bio_sex_other:        { en: 'Prefer not to say', zh: '不便透露' },
  bio_height:           { en: 'Height (cm)', zh: '身高 (cm)' },
  bio_weight:           { en: 'Weight (kg)', zh: '体重 (kg)' },
  bio_pregnant:         { en: 'Pregnant', zh: '孕期' },
  bio_lactating:        { en: 'Breastfeeding', zh: '哺乳期' },
  bio_allergies:        { en: 'Allergies / things to avoid', zh: '过敏或需要回避的成分' },
  bio_allergies_ph:     { en: 'e.g. shellfish, soy, peanuts', zh: '例如：贝类、大豆、花生' },
  bio_bmi:              { en: 'BMI', zh: 'BMI' },
  bio_save:             { en: 'Save', zh: '保存' },
  bio_clear:            { en: 'Clear', zh: '清除' },
  bio_saved:            { en: 'Details saved — dosing will be personalized.', zh: '信息已保存，剂量建议将个性化生成。' },
  bio_cleared:          { en: 'Details cleared.', zh: '信息已清除。' },
  bio_privacy:          { en: 'Stored on this device and sent only with your nutrition questions. General education, not a prescription.', zh: '仅保存在本设备，只在营养相关提问时发送。属于一般性教育信息，不构成处方。' },
} as const;


type Strings = typeof STRINGS;
type StringKey = keyof Strings;

// Resolved bilingual map for the active language.
export type AiTextMap = {
  [K in StringKey]: Strings[K] extends { en: infer E; zh: any }
    ? E
    : Strings[K] extends { en: infer E; zh: any }[]
      ? E[]
      : Strings[K] extends Record<'en' | 'zh', infer V>
        ? V
        : never;
};

/**
 * Resolve every AI conversation string for the given language. Call inside
 * a component like:
 *
 *   const { i18n } = useTranslation();
 *   const T = aiText(i18n.language);
 *   <p>{T.brand_concierge_name}</p>
 */
export function aiText(lang?: string): AiTextMap {
  const l = aiLangOf(lang);
  const out: any = {};
  for (const k of Object.keys(STRINGS) as StringKey[]) {
    const v: any = (STRINGS as any)[k];
    out[k] = v[l];
  }
  return out as AiTextMap;
}

/** Convenience for inline picks when only one key is needed. */
export const pickAi = <K extends StringKey>(key: K, lang?: string): AiTextMap[K] =>
  (STRINGS as any)[key][aiLangOf(lang)];
